<template>
  <div class="form-container">
    <h1 class="mb-4">建物情報新規作成</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="customer" class="form-label">顧客 <span class="badge text-bg-danger">必須</span></label>
        <select id="customer" class="form-select" v-model="building.m_customer_id" required>
          <option value="" disabled selected>選択してください</option> <!-- placeholder -->
          <option v-for="customer in customers" :key="customer.id" :value="customer.id">{{ customer.customer_nm }}</option>
        </select>
        <div v-if="errorMessages.m_customer_id" class="alert alert-danger mt-2">{{ errorMessages.m_customer_id[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="buildingType" class="form-label">区分 <span class="badge text-bg-danger">必須</span></label>
        <select id="buildingType" class="form-select" v-model="building.m_building_type_id" required>
          <option value="" disabled selected>選択してください</option> <!-- placeholder -->
          <option v-for="buildingType in buildingTypes" :key="buildingType.id" :value="buildingType.id">{{ buildingType.building_type_nm }}</option>
        </select>
        <div v-if="errorMessages.m_building_type_id" class="alert alert-danger mt-2">{{ errorMessages.m_building_type_id[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="buildingName" class="form-label">建物名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="building.building_nm" type="text" placeholder="建物名" class="form-control" id="buildingName" required>
        <div v-if="errorMessages.building_nm" class="alert alert-danger mt-2">{{ errorMessages.building_nm[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="building.postal_code"
        @address="({ region, locality, street }) => {
          building.prefecture = region;
          building.city = locality;
          building.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="building.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="building.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="building.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="building.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="contractDt" class="form-label">契約日</label>
        <input v-model="building.contract_dt" type="date" placeholder="契約日" class="form-control" id="contractDt">
        <div v-if="errorMessages.contract_dt" class="alert alert-danger mt-2">{{ errorMessages.contract_dt[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeNm" class="form-label">担当者</label>
        <input v-model="building.in_charge_nm" type="text" placeholder="担当者" class="form-control" id="inChargeNm">
        <div v-if="errorMessages.in_charge_nm" class="alert alert-danger mt-2">{{ errorMessages.in_charge_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargePhoneNum" class="form-label">携帯番号</label>
        <input v-model="building.in_charge_phone_num" type="text" placeholder="携帯番号" class="form-control" id="inChargePhoneNum">
        <div v-if="errorMessages.in_charge_phone_num" class="alert alert-danger mt-2">{{ errorMessages.in_charge_phone_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeEmail" class="form-label">担当者メールアドレス</label>
        <input v-model="building.in_charge_email" type="text" placeholder="担当者メールアドレス" class="form-control" id="inChargeEmail">
        <div v-if="errorMessages.in_charge_email" class="alert alert-danger mt-2">{{ errorMessages.in_charge_email[0] }}</div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-primary mx-1" :disabled="isSubmitting">登録</button>
      </div>
    </form>

    <DuplicateNameConfirmationModal
      :modalId="duplicateModalId"
      message="すでに同名の建物名が登録されていますが、本当に作成してもよろしいでしょうか？"
      @confirm="handleDuplicateConfirm"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import { checkBuildingDuplicate, getCustomers, getBuildingTypes, createBuilding } from '@/api/axios';
import DuplicateNameConfirmationModal from '@/components/shared/DuplicateNameConfirmationModal.vue';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';

export default {
  components: {
    DuplicateNameConfirmationModal,
    PostalCodeInput
  },
  setup() {
    const title = ref('建物情報新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const customers = ref([]);
    const buildingTypes = ref([]);
    const building = ref({
      m_customer_id: '',
      m_building_type_id: '',
      building_nm: '',
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      contract_dt: '',
      in_charge_nm: '',
      in_charge_phone_num: '',
      in_charge_email: ''
      // 他のフィールド
    });

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});
    const isSubmitting = ref(false);
    const duplicateModalId = 'duplicateBuildingCreateModal';

    const store = useStore();
    const router = useRouter();

    const fetchCustomerAndBuildingTypes = async () => {
      try {
        const response = await getCustomers({ include_visibility_hidden: true });
        customers.value = response.data.customers;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }

      try {
        const response = await getBuildingTypes({ include_visibility_hidden: true });
        buildingTypes.value = response.data.building_types;
      } catch (error) {
        console.error('建物種別情報ーデータの取得に失敗しました:', error);
      }
    };

    const showDuplicateModal = () => {
      const modalElement = document.getElementById(duplicateModalId);
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.show();
    };

    const saveBuilding = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        await createBuilding(building.value);
        store.commit('setFlashMessage', {message: '建物情報作成完了！', type: 'success'});
        router.push(`/buildings`);
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }

        store.commit('setFlashMessage', {message: '建物情報作成失敗！', type: 'danger'});
        console.error('作成エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        const response = await checkBuildingDuplicate(building.value.building_nm);
        if (response.data?.duplicate) {
          showDuplicateModal();
          return;
        }
      } catch (error) {
        console.error('重複チェックエラー:', error);
      } finally {
        isSubmitting.value = false;
      }

      await saveBuilding();
    };

    const handleDuplicateConfirm = async () => {
      await saveBuilding();
    };

    onMounted(fetchCustomerAndBuildingTypes);

    return {
      title,
      customers,
      buildingTypes,
      building,
      handleSubmit,
      handleDuplicateConfirm,
      errorMessages,
      isSubmitting,
      duplicateModalId
    };
  }
};
</script>

<style scoped>
.form-container {
  width: 70%;
  padding: 20px 20px 80px 20px;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

input[type="text"],
input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 10px;
  margin-bottom: 4px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.btn-area{
  text-align: center;
}
</style>
